import { useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import { usePostHog } from "posthog-js/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import config from "config";

import { useSafeState } from "hooks/useSafeState";
import { useFacebookPixel } from "components/FacebookPixelProvider";

// ===========================================================
// Cal.com Embed Component
// ===========================================================

type Analytics = {
  facebook?: {
    event: string;
    eventId?: string;
    data?: {
      content_name?: string;
      content_category?: string;
    };
  };
};

export default function CalEmbed({
  afterId,
  eventUrl,
  analytics,
  buttonContent,
  onEvent,
  prefillData,
  dataPayload,
  isInline = false,
}: {
  afterId: string;
  eventUrl: string;
  analytics?: Analytics;
  buttonContent: ReactNode;
  onEvent?: (eventType: string) => void;
  prefillData?: Record<string, unknown>;
  dataPayload?: string;
  isInline?: boolean;
}) {
  const { trackEvent } = useFacebookPixel();
  const posthog = usePostHog();
  const [isOpen, setIsOpen] = useSafeState(false);

  // Handle Cal.com iframe events
  useEffect(() => {
    const handleEvent = (event: MessageEvent) => {
      // Validate origin to prevent malicious postMessage attacks
      const expectedOrigin = new URL(config.formHost).origin;
      if (event.origin !== expectedOrigin) {
        return;
      }

      const isCalEvent = event.data?.id === "form-submit";

      if (!isCalEvent) {
        return;
      }

      console.log("[cal:event]", event.data);

      // Track scheduling event
      posthog?.capture(`cal_call_scheduled`, { id: afterId });

      if (analytics?.facebook) {
        const { event: fbEvent, eventId, data } = analytics.facebook;

        trackEvent({
          event: fbEvent,
          eventId,
          data,
        });
      }

      Sentry.addBreadcrumb({
        category: "cal.event_scheduled",
        message: "Cal.com event scheduled",
        level: "info",
      });
      Sentry.setTag("cal.scheduled_call", "true");

      if (onEvent) {
        onEvent("cal.event_scheduled");
      }

      // Close modal after scheduling
      if (!isInline) {
        setIsOpen(false);
      }
    };

    window.addEventListener("message", handleEvent);

    return () => {
      window.removeEventListener("message", handleEvent);
    };
  }, [afterId, analytics, isInline, onEvent, posthog, setIsOpen, trackEvent]);

  // Handle escape key to close modal and prevent body scrolling
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, setIsOpen]);

  function handleClick() {
    posthog?.capture("cta_button_click", {
      id: "schedule_a_call",
      cta_button_id: "schedule_a_call",
    });
    setIsOpen(true);
  }

  // Build iframe URL with query parameters
  const buildIframeUrl = () => {
    const url = new URL(eventUrl);

    // Add prefill data as query parameters
    if (prefillData) {
      Object.entries(prefillData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    // Add data payload
    if (dataPayload) {
      url.searchParams.set("ped", dataPayload);
    }

    return url.toString();
  };

  const iframeUrl = buildIframeUrl();

  if (isInline) {
    return (
      <div className="h-full min-h-96 w-full">
        <iframe
          id={`cal_embed_${afterId}`}
          sandbox="allow-top-navigation allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
          src={iframeUrl}
          seamless={true}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: "600px",
          }}
          title="Schedule a call"
        />
      </div>
    );
  }

  const modalContent = isOpen ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={() => setIsOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Schedule a call"
    >
      <div
        className="relative h-[90vh] w-full max-w-4xl rounded-lg bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-soil text-white hover:bg-evergreen"
          onClick={() => setIsOpen(false)}
          aria-label="Close modal"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        <iframe
          id={`cal_embed_modal_${afterId}`}
          sandbox="allow-top-navigation allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
          src={iframeUrl}
          seamless={true}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            borderRadius: "0.5rem",
          }}
          title="Schedule a call"
        />
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div
        tabIndex={0}
        role="button"
        className="inline-block w-full sm:w-auto"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {buttonContent}
      </div>

      {/* Portal modal to document.body to escape stacking contexts */}
      {modalContent && createPortal(modalContent, document.body)}
    </div>
  );
}
